import React, { useRef } from 'react';

import { UserData } from '@client/store/globalStore/state/initialGlobalState';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { AutocompleteOptionWithChart, RespButton } from '@ledium/ui-common';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';
import { Box, Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles({
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 8px'
    },
    info: {
        flex: 1,
        minWidth: 0
    },
    actions: {
        display: 'flex',
        flexShrink: 0,
        gap: '4px'
    }
});

interface LightChannelItem {
    id: string;
    name: string;
    channel: number;
    resolution: number;
    locked?: unknown;
    linked?: unknown;
}

interface LightChannelListProps {
    itemList: LightChannelItem[] | undefined;
    onChange?: (item: LightChannelItem | null) => void;
    onView: (item: LightChannelItem) => void;
    onEdit: (item: LightChannelItem) => void;
    onDelete: (item: LightChannelItem) => void;
    onDuplicate: (item: LightChannelItem) => void;
    allowDuplicate?: boolean;
    userData: UserData;
}

const LightChannelList = ({ itemList, onView, onEdit, onDelete, onDuplicate, allowDuplicate, userData }: LightChannelListProps) => {
    const classes = useStyles();
    const {
        pageState: { pageConfig }
    } = usePageStore();
    const testingIds = useRef(new Set<string>());

    function testChannel(item: LightChannelItem) {
        if (!testingIds.current.has(item.id)) {
            testingIds.current.add(item.id);
            lediumApi.get(`lightChannels/testDmx/${item.channel}/${item.resolution}`).then(() => {
                testingIds.current.delete(item.id);
            });
        }
    }

    if (!itemList || !itemList.length) {
        return null;
    }

    return (
        <div>
            {itemList.map((item, idx) => (
                <React.Fragment key={item.id}>
                    {idx > 0 && <Divider />}
                    <div className={classes.row} onDoubleClick={() => (item.locked ? onView(item) : onEdit(item))}>
                        <Box className={classes.info}>
                            <AutocompleteOptionWithChart errorCodes={pageConfig.errorCodes} option={item} size="large" />
                        </Box>
                        <div className={classes.actions}>
                            <RespButton variant="contained" color="primary" onClick={() => onView(item)} startIcon={<VisibilityIcon />}>
                                {i18n('view')}
                            </RespButton>
                            {userData?.admin && (
                                <RespButton variant="contained" color="primary" disabled={!!item.locked} onClick={() => onEdit(item)} startIcon={<EditIcon />}>
                                    {i18n('edit')}
                                </RespButton>
                            )}
                            {userData?.admin && allowDuplicate && (
                                <RespButton variant="contained" color="primary" onClick={() => onDuplicate(item)} startIcon={<CallSplitIcon />}>
                                    {i18n('duplicate')}
                                </RespButton>
                            )}
                            <RespButton variant="contained" color="primary" onClick={() => testChannel(item)} startIcon={<WbIncandescentIcon />}>
                                {i18n('test')}
                            </RespButton>
                            {userData?.admin && (
                                <RespButton
                                    variant="contained"
                                    color="secondary"
                                    disabled={!!item.locked || !!item.linked}
                                    onClick={() => onDelete(item)}
                                    startIcon={<DeleteIcon />}
                                >
                                    {i18n('delete')}
                                </RespButton>
                            )}
                        </div>
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
};

export default LightChannelList;
